import { useEffect, useRef, useState } from 'react';
import {
  WELL_KNOWN_FEATIT_PATH,
  appSetupLinks,
  type IAppSetupLinks,
  type IFeatitWellKnown,
} from '@featit/shared/app-setup';
import { API_BASE_URL } from '@/services/ApiService';

export type AppSetupStateType =
  | { phase: 'loading' }
  | { phase: 'ready'; identity: IFeatitWellKnown; links: IAppSetupLinks }
  | { phase: 'foreign' }
  | { phase: 'failed' };

/** The API's absolute address: in a browser `API_BASE_URL` is a path on this page's origin. */
function apiBase(): URL {
  return new URL(`${API_BASE_URL.replace(/\/+$/, '')}/`, window.location.origin);
}

/** The deployment this page is served by, as it describes itself, from the root of its origin. */
export async function readIdentity(): Promise<IFeatitWellKnown> {
  const response = await fetch(new URL(WELL_KNOWN_FEATIT_PATH, apiBase()).toString(), {
    headers: { accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`/.well-known/featit answered ${response.status}`);
  return (await response.json()) as IFeatitWellKnown;
}

/** Where the setup file downloads from. */
export function appSetupFileUrl(): string {
  return new URL('app/setup-file', apiBase()).toString();
}

/**
 * Copies a setup link to the clipboard, and says so for 2 seconds (I-1047). Shared between
 * `AppSetupPage` (the public landing page) and `MobileAppSettings` (Settings → Phone and tablet
 * app, I-1047's inline copy) so the two do not carry two copies of the same timeout dance.
 */
export function useCopyAppSetupLink(): { copied: boolean; copyLink: (link: string) => void } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function copyLink(link: string) {
    // `navigator.clipboard` itself can be absent (an insecure context, an older WebKit) — guarded
    // here rather than left to throw synchronously before the `.catch` below ever attaches.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard) {
      setCopied(false);
      return;
    }
    clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        if (timer.current !== undefined) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  }

  return { copied, copyLink };
}

/**
 * This deployment as it describes itself, with the links that add it to the app. `wanted` is the
 * deployment a setup link names: a link for another one is refused, because following it would add
 * this one instead. `pairRequestId` is the pairing request a *Connect a mobile device* QR code named
 * (F-0332): the app link carries it on, so the page a scan lands on never drops it.
 */
export function useAppSetup(
  wanted: string | null = null,
  pairRequestId: string | null = null,
): AppSetupStateType {
  const [state, setState] = useState<AppSetupStateType>({ phase: 'loading' });
  useEffect(() => {
    let live = true;
    readIdentity()
      .then((identity) => {
        if (!live) return;
        if (wanted && wanted !== identity.deploymentId) setState({ phase: 'foreign' });
        else
          setState({
            phase: 'ready',
            identity,
            links: appSetupLinks({
              origin: identity.origin,
              deploymentId: identity.deploymentId,
              ...(pairRequestId ? { pairRequestId } : {}),
            }),
          });
      })
      .catch(() => live && setState({ phase: 'failed' }));
    return () => {
      live = false;
    };
  }, [wanted, pairRequestId]);
  return state;
}
